/**
 * HOOK: useFormFields
 * Gestion des champs d'un formulaire avec templates
 * Supporte le drag & drop avec réordonnancement
 */

import { useState, useEffect, useCallback } from 'react';
import type { FormFieldWithValues, CreateFormFieldDto, UpdateFormFieldDto } from '../types/form';
import { mergeFieldWithTemplate } from '../types/form';
import { useSupabase } from './useSupabase';

interface UseFormFieldsOptions {
  formId?: string;
  autoLoad?: boolean;
}

export function useFormFields(options: UseFormFieldsOptions = {}) {
  const { formId, autoLoad = true } = options;
  const supabase = useSupabase();

  const [fields, setFields] = useState<FormFieldWithValues[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Charge les champs d'un formulaire avec leurs templates
   */
  const loadFields = useCallback(async () => {
    if (!formId) {
      setFields([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: rawFields, error: fetchError } = await supabase
        .from('form_fields')
        .select('*, field_template:field_templates(*)')
        .eq('form_id', formId)
        .order('order_index', { ascending: true });

      if (fetchError) throw fetchError;

      // Fusionner les valeurs des templates avec les overrides
      const fieldsWithValues: FormFieldWithValues[] = (rawFields || []).map((field: any) =>
        mergeFieldWithTemplate(field)
      );

      setFields(fieldsWithValues);
    } catch (err) {
      console.error('Error loading form fields:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [formId, supabase]);

  // Charger au montage et quand formId change
  useEffect(() => {
    if (autoLoad) {
      loadFields();
    }
  }, [autoLoad, loadFields]);

  /**
   * Crée un nouveau champ (association template)
   */
  const createField = useCallback(
    async (data: CreateFormFieldDto): Promise<boolean> => {
      try {
        const { error: createError } = await supabase.from('form_fields').insert(data);

        if (createError) {
          console.error('Error creating field:', createError);
          return false;
        }

        await loadFields();
        return true;
      } catch (err) {
        console.error('Error creating field:', err);
        return false;
      }
    },
    [supabase, loadFields]
  );

  /**
   * Met à jour un champ (overrides)
   */
  const updateField = useCallback(
    async (id: string, data: UpdateFormFieldDto): Promise<boolean> => {
      try {
        const { error: updateError } = await supabase.from('form_fields').update(data).eq('id', id);

        if (updateError) {
          console.error('Error updating field:', updateError);
          return false;
        }

        await loadFields();
        return true;
      } catch (err) {
        console.error('Error updating field:', err);
        return false;
      }
    },
    [supabase, loadFields]
  );

  /**
   * Supprime un champ
   */
  const deleteField = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error: deleteError } = await supabase.from('form_fields').delete().eq('id', id);

        if (deleteError) {
          console.error('Error deleting field:', deleteError);
          return false;
        }

        await loadFields();
        return true;
      } catch (err) {
        console.error('Error deleting field:', err);
        return false;
      }
    },
    [supabase, loadFields]
  );

  /**
   * Réordonne les champs après un drag & drop
   * Utilise une transaction avec contrainte DEFERRED pour éviter les conflits
   */
  const reorderFields = useCallback(
    async (reorderedFields: FormFieldWithValues[]): Promise<boolean> => {
      try {
        // Utiliser une transaction RPC pour éviter les conflits de contrainte unique
        const updates = reorderedFields.map((field, index) => ({
          id: field.id,
          order_index: index,
        }));

        // Construire la requête SQL pour la transaction
        const updateQueries = updates
          .map((u) => `UPDATE form_fields SET order_index = ${u.order_index} WHERE id = '${u.id}'`)
          .join('; ');

        const { error } = await supabase.rpc('exec_sql', {
          query: `
            BEGIN;
            SET CONSTRAINTS unique_field_order DEFERRED;
            ${updateQueries};
            COMMIT;
          `,
        });

        if (error) {
          console.error('Error reordering fields:', error);

          // Fallback : méthode alternative sans RPC
          // Mettre temporairement des order_index négatifs, puis les vraies valeurs
          for (let i = 0; i < updates.length; i++) {
            await supabase
              .from('form_fields')
              .update({ order_index: -(i + 1000) })
              .eq('id', updates[i].id);
          }

          for (let i = 0; i < updates.length; i++) {
            await supabase
              .from('form_fields')
              .update({ order_index: updates[i].order_index })
              .eq('id', updates[i].id);
          }
        }

        // Rafraîchir les données
        await loadFields();
        return true;
      } catch (error) {
        console.error('Error reordering fields:', error);
        return false;
      }
    },
    [supabase, loadFields]
  );

  return {
    // Données
    fields,
    loading,
    error,

    // Opérations CRUD
    createField,
    updateField,
    deleteField,
    refreshFields: loadFields,

    // Opérations spécifiques
    reorderFields,
  };
}
